// src/components/warnings/WarningBanner.jsx
// One pipeline warning, worded by warningCopy.js and coloured by its severity.
// BRAND_TABOO_IN_TEXT / TERMINOLOGY_MISMATCH items show the sentence with the fact
// marked, where it sits and «Usa: …» (decision: shown in the warning, not inside the
// renderers). CITED_SOURCE_NOT_ACCESSIBLE lists its [N] references.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, AlertCircle, Info } from 'lucide-react';
import { friendlyWarningCopy, severityTone } from './warningCopy.js';
import HighlightedText from '../claims/HighlightedText.jsx';
import { locationLabel } from '../claims/locationLabel.js';
import { COLORS, FONT_MONO, RADII, SEMANTIC } from '../../constants/theme.js';

const ICONS = { errore: AlertCircle, attenzione: AlertTriangle, neutro: Info };

export default function WarningBanner({ warning, t = {} }) {
  const { t: tr } = useTranslation('outputs');
  const { title, message, urls, items, refs } = friendlyWarningCopy(warning, tr);
  const tone = severityTone(warning.severity);
  const palette = SEMANTIC[tone];
  const Icon = ICONS[tone];
  const muted = t.textMuted || COLORS.textTertiary;
  return (
    <div
      role={tone === 'errore' ? 'alert' : undefined}
      data-tone={tone}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px 16px',
        borderRadius: RADII.controlInner, background: palette.bg, border: `1px solid ${palette.bg}`,
      }}
    >
      <Icon size={16} color={palette.fg} style={{ flexShrink: 0, marginTop: '1px' }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '11px', fontWeight: 700, color: palette.fg, fontFamily: FONT_MONO, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
          {title}
        </div>
        <div style={{ fontSize: '13px', color: t.text || COLORS.borderStrong, lineHeight: 1.5 }}>{message}</div>
        {urls?.length > 0 && (
          <ul style={{ margin: '8px 0 0 0', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {urls.map((u, i) => (
              <li key={i} style={{ fontSize: '11px', color: muted, fontFamily: FONT_MONO, wordBreak: 'break-all', lineHeight: 1.4 }}>
                • {u.url}
              </li>
            ))}
          </ul>
        )}
        {items?.length > 0 && (
          <ul style={{ margin: '8px 0 0 0', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {items.map((item, i) => (
              <li key={i} style={{ fontSize: '12.5px', lineHeight: 1.5, color: t.text || COLORS.ink }}>
                <HighlightedText text={item.text} highlight={item.highlight} tone={tone} />
                <div style={{ fontSize: '11px', color: muted, marginTop: '2px' }}>
                  {locationLabel(item.location, tr)}
                  {item.claim ? ` · ${tr('warnings.itemClaim', { claim: item.claim })}` : ''}
                  {item.correction ? ` · ${tr('warnings.itemUse', { term: item.correction })}` : ''}
                </div>
              </li>
            ))}
          </ul>
        )}
        {refs?.length > 0 && (
          <ul style={{ margin: '8px 0 0 0', padding: 0, listStyle: 'none' }}>
            {refs.map((ref, i) => (
              <li key={i} style={{ fontSize: '12px', color: t.text || COLORS.ink }}>{ref}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
